/**
 * Application Insights Telemetry Service
 * 
 * This module initializes and exports the Application Insights SDK for React.
 * It provides telemetry tracking for page views, custom events, exceptions, and metrics.
 * 
 * @see https://learn.microsoft.com/en-us/azure/azure-monitor/app/javascript-framework-extensions
 */

import { ApplicationInsights } from '@microsoft/applicationinsights-web';
import { ReactPlugin } from '@microsoft/applicationinsights-react-js';

// Initialize the React plugin
const reactPlugin = new ReactPlugin();

// Last dependency traceparent observed (used for user-facing debug modal)
let lastTraceparent = null;

/**
 * Returns the last captured W3C `traceparent` value from AppInsights dependency tracking.
 * Note: This is best-effort and depends on AppInsights distributed tracing configuration.
 */
export const getLastTraceparent = () => lastTraceparent;

const extractHex = (str, len) => {
  if (!str) return null;
  const re = new RegExp(`([0-9a-fA-F]{${len}})`);
  const m = String(str).match(re);
  return m ? m[1].toLowerCase() : null;
};

const tryComputeTraceparent = (telemetryItem) => {
  if (!telemetryItem) return null;

  const baseData = telemetryItem.baseData || telemetryItem.data?.baseData;
  const tags = telemetryItem.tags || telemetryItem.data?.tags || {};

  // If the SDK already captured it somewhere in properties, use that.
  const props = baseData?.properties || {};
  const direct =
    props.traceparent ||
    props.traceParent ||
    props["traceparent"] ||
    props["traceParent"];
  if (direct) return String(direct);

  // Derive from operation trace id + dependency span id when possible.
  // Important: Track where traceId came from to avoid using the same source for spanId.
  let traceId = extractHex(tags["ai.operation.id"], 32);
  let traceIdFromBaseDataId = false;
  
  if (!traceId) {
    traceId = extractHex(baseData?.traceId, 32);
  }
  if (!traceId) {
    traceId = extractHex(baseData?.id, 32);
    traceIdFromBaseDataId = !!traceId;
  }

  // Only use baseData?.id for spanId if we didn't use it for traceId.
  // Otherwise, spanId would be the first half of traceId, which is semantically incorrect.
  const spanId = traceIdFromBaseDataId
    ? extractHex(tags["ai.operation.parentId"], 16)
    : (extractHex(baseData?.id, 16) || extractHex(tags["ai.operation.parentId"], 16));

  if (!traceId || !spanId) return null;
  return `00-${traceId}-${spanId}-01`;
};

// Get connection string from environment variable
// Note: Must use REACT_APP_ prefix for Create React App to include it in the build
const connectionString = process.env.REACT_APP_APPINSIGHTS_CONNECTION_STRING;

/**
 * Get current user info from localStorage
 * @returns {Object} User info object with email, name, and role
 */
const getUserInfo = () => {
  const email = localStorage.getItem('email');
  const fullName = localStorage.getItem('full_name');
  
  // Determine user role based on which token exists
  let role = 'anonymous';
  if (localStorage.getItem('adminToken')) {
    role = 'admin';
  } else if (localStorage.getItem('employeeToken')) {
    role = 'employee';
  } else if (localStorage.getItem('authToken')) {
    role = 'customer';
  }
  
  return {
    userEmail: email || null,
    userName: fullName || null,
    userRole: role,
    isAuthenticated: role !== 'anonymous'
  };
};

// Initialize Application Insights
// Note: We use enableAutoRouteTracking instead of passing a history object
// because React Router v6 manages its own internal history and doesn't support
// custom history objects. enableAutoRouteTracking listens to browser URL changes
// directly, which works correctly with any router implementation.
const appInsights = new ApplicationInsights({
  config: {
    connectionString: connectionString,
    // Enable automatic route tracking for page views
    // This listens to browser URL changes directly (works with React Router v6)
    enableAutoRouteTracking: true,
    // Extensions
    extensions: [reactPlugin],
    // Disable telemetry if no connection string is provided (local dev)
    disableTelemetry: !connectionString,
    // Enable correlation headers for distributed tracing
    enableCorsCorrelation: true,
    // Only add correlation headers to LaserBros domains to avoid CORS issues with third-party APIs
    correlationHeaderDomains: ['*.laserbros.com'],
    // Enable request header tracking
    enableRequestHeaderTracking: true,
    // Enable response header tracking
    enableResponseHeaderTracking: true,
    // Auto-track dependencies (AJAX, fetch)
    disableFetchTracking: false,
    disableAjaxTracking: false,
    // Maximum batch size before sending
    maxBatchSizeInBytes: 10000,
    // Maximum batch interval in ms
    maxBatchInterval: 15000,
    // Sampling percentage (100 = send all telemetry)
    samplingPercentage: 100,
  }
});

// Load Application Insights only if connection string is provided
if (connectionString) {
  appInsights.loadAppInsights();
  
  // Add telemetry initializer to enrich ALL telemetry with user context and cloud role
  appInsights.addTelemetryInitializer((envelope) => {
    // Set Cloud Role Name and Instance for better identification in App Insights
    // These help distinguish this frontend from other services in distributed tracing
    envelope.tags = envelope.tags || {};
    envelope.tags['ai.cloud.role'] = process.env.REACT_APP_APPINSIGHTS_ROLE_NAME || 'LaserBros-Frontend';
    envelope.tags['ai.cloud.roleInstance'] = window.location.hostname;
    
    const userInfo = getUserInfo();
    
    // Add user properties to all telemetry
    if (envelope.data) {
      envelope.data.userEmail = userInfo.userEmail;
      envelope.data.userName = userInfo.userName;
      envelope.data.userRole = userInfo.userRole;
      envelope.data.isAuthenticated = userInfo.isAuthenticated;
    }
    
    // Also add to baseData for custom events
    if (envelope.baseData && envelope.baseData.properties) {
      envelope.baseData.properties.userEmail = userInfo.userEmail;
      envelope.baseData.properties.userName = userInfo.userName;
      envelope.baseData.properties.userRole = userInfo.userRole;
      envelope.baseData.properties.isAuthenticated = userInfo.isAuthenticated;
    }

    // Capture last outgoing dependency traceparent (best-effort) for support/debug UI.
    // Dependency telemetry uses baseType = "RemoteDependencyData".
    if (envelope.baseType === "RemoteDependencyData") {
      const tp = tryComputeTraceparent(envelope);
      if (tp) lastTraceparent = tp;
    }
    
    return true; // Return true to include this telemetry item
  });
  
  // Check if user is already logged in on initialization and set context
  const initUserInfo = getUserInfo();
  if (initUserInfo.isAuthenticated && initUserInfo.userEmail) {
    appInsights.setAuthenticatedUserContext(initUserInfo.userEmail, null, true);
  }
  
  // Set authenticated user context if available
  const setUserContext = (userId, accountId) => {
    if (userId) {
      appInsights.setAuthenticatedUserContext(userId, accountId, true);
    }
  };

  // Clear user context on logout
  const clearUserContext = () => {
    appInsights.clearAuthenticatedUserContext();
  };

  // Export user context helpers
  appInsights.setUserContext = setUserContext;
  appInsights.clearUserContext = clearUserContext;
}

/**
 * Get user properties to include in telemetry
 * Note: The telemetry initializer already adds these, but this ensures
 * they're available for immediate tracking before the initializer runs
 */
const getUserProperties = () => {
  const userInfo = getUserInfo();
  return {
    userEmail: userInfo.userEmail,
    userName: userInfo.userName,
    userRole: userInfo.userRole,
    isAuthenticated: userInfo.isAuthenticated
  };
};

/**
 * Track a custom event
 * @param {string} name - Event name
 * @param {Object} properties - Custom properties
 * @param {Object} measurements - Custom measurements
 */
export const trackEvent = (name, properties = {}, measurements = {}) => {
  if (connectionString) {
    const userProps = getUserProperties();
    appInsights.trackEvent({ 
      name, 
      properties: { ...userProps, ...properties },
      measurements 
    });
  }
};

/**
 * Track a page view
 * @param {string} name - Page name
 * @param {string} uri - Page URI (optional)
 * @param {Object} properties - Custom properties
 */
export const trackPageView = (name, uri, properties = {}) => {
  if (connectionString) {
    const userProps = getUserProperties();
    appInsights.trackPageView({ 
      name, 
      uri, 
      properties: { ...userProps, ...properties } 
    });
  }
};

/**
 * Track an exception
 * @param {Error} exception - The exception object
 * @param {Object} properties - Custom properties
 */
export const trackException = (exception, properties = {}) => {
  if (connectionString) {
    const userProps = getUserProperties();
    appInsights.trackException({ 
      exception, 
      properties: { ...userProps, ...properties } 
    });
  }
};

/**
 * Track a custom metric
 * @param {string} name - Metric name
 * @param {number} average - The metric value
 * @param {Object} properties - Custom properties
 */
export const trackMetric = (name, average, properties = {}) => {
  if (connectionString) {
    const userProps = getUserProperties();
    appInsights.trackMetric({ 
      name, 
      average,
      properties: { ...userProps, ...properties }
    });
  }
};

/**
 * Track a trace message
 * @param {string} message - Trace message
 * @param {Object} properties - Custom properties
 */
export const trackTrace = (message, properties = {}) => {
  if (connectionString) {
    const userProps = getUserProperties();
    appInsights.trackTrace({ 
      message, 
      properties: { ...userProps, ...properties }
    });
  }
};

/**
 * Start tracking a custom event duration
 * @param {string} name - Event name
 * @returns {Function} - Call this function to stop tracking and record the duration
 */
export const startTrackEvent = (name) => {
  const startTime = Date.now();
  return (properties = {}) => {
    const duration = Date.now() - startTime;
    trackEvent(name, { ...properties, durationMs: duration });
  };
};

/**
 * Flush all pending telemetry
 */
export const flushTelemetry = () => {
  if (connectionString) {
    appInsights.flush();
  }
};

// Export getUserInfo for use in other modules if needed
export { getUserInfo };

export { reactPlugin, appInsights };
export default appInsights;

